import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, User, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import CartPreview from '@/components/CartPreview';

export default function Header() {
  const { user, logout, isAuthenticated, isArtist } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/70 border-b border-border transition-all duration-300">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="font-serif text-2xl font-bold tracking-tight hover:opacity-70 transition-opacity">
            THE MODERN GALLERY
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/gallery" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-gallery-link">
              GALLERY
            </Link>
            <Link to="/gallery/mature" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-nsfw-link">
              NSFW COLLECTION
            </Link>
            <Link to="/about" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-about-link">
              ABOUT
            </Link>
            <Link to="/contact" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-contact-link">
              CONTACT
            </Link>
            {isArtist && (
              <Link to="/artist/dashboard" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-artist-dashboard-link">
                DASHBOARD
              </Link>
            )}
            {isAuthenticated && !isArtist && (
              <Link to="/dashboard" className="text-sm font-medium hover:text-accent transition-colors" data-testid="nav-user-dashboard-link">
                MY ORDERS
              </Link>
            )}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {/* Cart is always visible for non-artists */}
            {!isArtist && <CartPreview />}
            
            {isAuthenticated ? (
              <>
                {!isArtist && (
                  <Link to="/favorites" data-testid="nav-favorites-btn">
                    <Button variant="ghost" size="icon">
                      <Heart className="h-5 w-5" />
                    </Button>
                  </Link>
                )}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">{user?.name}</span>
                  <Button onClick={handleLogout} variant="outline" size="sm" data-testid="logout-btn">
                    LOGOUT
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" data-testid="nav-login-btn">
                  <Button variant="ghost" size="sm">
                    LOGIN
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register-btn">
                  <Button className="rounded-full" size="sm">
                    SIGN UP
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <Link
                to="/gallery"
                className="text-sm font-medium hover:text-accent transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                GALLERY
              </Link>
              <Link
                to="/gallery/mature"
                className="text-sm font-medium hover:text-accent transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                NSFW COLLECTION
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium hover:text-accent transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                ABOUT
              </Link>
              <Link
                to="/contact"
                className="text-sm font-medium hover:text-accent transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                CONTACT
              </Link>
              {isArtist && (
                <Link
                  to="/artist/dashboard"
                  className="text-sm font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  DASHBOARD
                </Link>
              )}
              {!isArtist && (
                <Link
                  to="/cart"
                  className="text-sm font-medium hover:text-accent transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  CART
                </Link>
              )}
              {isAuthenticated && !isArtist && (
                <>
                  <Link
                    to="/dashboard"
                    className="text-sm font-medium hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    MY ORDERS
                  </Link>
                  <Link
                    to="/favorites"
                    className="text-sm font-medium hover:text-accent transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    FAVORITES
                  </Link>
                </>
              )}
              {isAuthenticated ? (
                <Button onClick={handleLogout} variant="outline" size="sm" className="w-full">
                  LOGOUT
                </Button>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full">
                      LOGIN
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="rounded-full w-full" size="sm">
                      SIGN UP
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}